import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import api from "@/lib/api";
import { BLOG_CATEGORIES } from "@/lib/constants";
import Seo from "@/components/Seo";
import CTABand from "@/components/sections/CTABand";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [cat, setCat] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/blog/posts", { params: cat ? { category: cat } : {} });
        setPosts(data);
      } finally { setLoading(false); }
    };
    load();
  }, [cat]);

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog · IA, SEO, automatisation pour PME"
        description="Conseils pratiques en intelligence artificielle, marketing numérique, SEO local et automatisation pour les PME au Québec."
        path="/blog"
      />
      <section className="bg-white pt-16 pb-8">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          <div className="text-[#00B8D9] text-xs tracking-[0.2em] uppercase font-bold mb-3">Blog</div>
          <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-[#0B1F3A] tracking-tighter max-w-3xl text-balance">
            Ressources pour <span className="text-[#00B8D9]">PME ambitieuses</span>
          </h1>
          <p className="mt-5 text-lg text-[#4B5563] max-w-2xl">
            Conseils pratiques sur l'IA, le SEO, l'automatisation et la croissance numérique.
          </p>

          <div className="mt-8 flex flex-wrap gap-2">
            <button onClick={() => setCat(null)} data-testid="blog-cat-all" className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${!cat ? "bg-[#0B1F3A] text-white" : "bg-[#F5F7FA] text-[#0B1F3A] hover:bg-[#0B1F3A]/10"}`}>Toutes</button>
            {BLOG_CATEGORIES.map((c) => (
              <button key={c} onClick={() => setCat(c)} data-testid={`blog-cat-${c.slice(0, 5)}`} className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${cat === c ? "bg-[#0B1F3A] text-white" : "bg-[#F5F7FA] text-[#0B1F3A] hover:bg-[#0B1F3A]/10"}`}>{c}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#F5F7FA] py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-10">
          {loading ? (
            <div className="text-center py-16 text-[#4B5563]">Chargement...</div>
          ) : posts.length === 0 ? (
            <div className="text-center py-16 text-[#4B5563]">Aucun article dans cette catégorie.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {posts.map((p, i) => (
                <motion.div key={p.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}>
                  <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group block h-full bg-white rounded-2xl overflow-hidden border border-gray-200 hover:border-[#00B8D9] hover:-translate-y-1 hover:shadow-lg transition-all duration-300">
                    {p.cover_image && (
                      <div className="aspect-video overflow-hidden bg-[#F5F7FA]">
                        <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                      </div>
                    )}
                    <div className="p-6">
                      <div className="flex items-center gap-3 text-xs text-[#4B5563] mb-3">
                        <span className="px-2.5 py-1 rounded-full bg-[#00B8D9]/10 text-[#00B8D9] font-semibold">{p.category}</span>
                        <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{new Date(p.created_at).toLocaleDateString("fr-CA")}</span>
                      </div>
                      <h3 className="font-heading font-bold text-lg text-[#0B1F3A] mb-2 leading-snug">{p.title}</h3>
                      <p className="text-sm text-[#4B5563] line-clamp-3 mb-4">{p.excerpt}</p>
                      <div className="text-sm font-semibold text-[#00B8D9] flex items-center gap-1 group-hover:gap-2 transition-all">
                        Lire l'article <ArrowRight className="h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
      <CTABand />
    </div>
  );
}
